import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, useCanShowCanvasPanel } from '@heidi/utils';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useDocumentDownloadUrl } from '../../../data/library/user-documents/hooks/useDocumentDownloadUrl';
import { documentPreviewAtom } from '../user-documents/atoms';
import { DocumentEmailDialog } from '../user-documents/document-email-dialog';
import { useDocumentEmail } from '../user-documents/hooks/useDocumentEmail';
// eslint-disable-next-line @heidi/rules/must-lazy-load-modals -- static on purpose: this import was static inside Chat and lazying it now would add a chunk boundary this relocation is required to leave unchanged
import { DocumentPreviewContent } from './DocumentPreviewContent';

/**
 * DocumentPreviewModal - Modal for previewing uploaded documents.
 *
 * Fetches the document URL from a document ID, then delegates to
 * DocumentPreviewContent for the shared header/content/footer UI.
 *
 * On fullscreen chat routes (desktop), the CanvasPanel handles preview instead
 * of this modal — so this component returns null in that context.
 *
 * `pathname` is the mount's route rather than a `useRouter()` read: a package
 * `/ui` zone may not import `next/router`, and the branch below is unchanged.
 */
export interface DocumentPreviewModalProps {
  /** The mounting route's `router.pathname`. */
  pathname: string;
}

export function DocumentPreviewModal({ pathname }: DocumentPreviewModalProps) {
  const { t } = useTranslations();
  const canShowCanvas = useCanShowCanvasPanel();
  const [previewState, setPreviewState] = useAtom(documentPreviewAtom);
  const { isOpen, documentId } = previewState;
  const emailState = useDocumentEmail(documentId);

  const {
    data: downloadData,
    isLoading,
    error,
  } = useDocumentDownloadUrl(isOpen ? documentId : null);

  const handleClose = useCallback(() => {
    setPreviewState({ isOpen: false, documentId: null });
  }, [setPreviewState]);

  const handleDownload = useCallback(() => {
    if (downloadData?.url) {
      window.open(downloadData.url, '_blank');
    }
  }, [downloadData?.url]);

  // On fullscreen Evidence chat routes (desktop), CanvasPanel handles preview
  // instead — but Work projects pages mount this modal explicitly and need it.
  const isProjectsRoute = pathname.includes('/chat/projects');
  const isFullScreenEvidence = pathname.includes('/chat');
  if (isFullScreenEvidence && canShowCanvas && !isProjectsRoute) {
    return null;
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent
          className={cn(
            'flex h-[90vh] max-h-[90vh] w-[90vw] max-w-6xl flex-col gap-0 overflow-hidden p-0',
            'rounded-2xl bg-surface-accent sm:rounded-2xl'
          )}
          hideCloseIcon
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            {downloadData?.filename || t('chat.documentPreview')}
          </DialogTitle>
          <DocumentPreviewContent
            title={downloadData?.filename || t('chat.documentPreview')}
            url={downloadData?.url || null}
            filename={downloadData?.filename || null}
            isLoading={isLoading}
            error={error}
            onClose={handleClose}
            onDownload={handleDownload}
            onEmail={emailState.openEmailDialog}
            variant="modal"
          />
        </DialogContent>
      </Dialog>
      <DocumentEmailDialog emailState={emailState} />
    </>
  );
}
